import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { IListagemUsuario, listKeyValueUsuarioStatus } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { getValueFromKeyValue, stringToDateTime } from 'shared/utils';
import { useHasPermissionAcao } from 'shared/hooks';
import { UsuariosService } from 'shared/services/usuarios/usuariosService';

export const ListagemDeUsuario: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const hasPermitionCreate = useHasPermissionAcao('usuario','CRE');  
  const hasPermitionRead = useHasPermissionAcao('usuario','REA');
  
  const [rows, setRows] = useState<IListagemUsuario[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaId = useMemo(() => {
    return searchParams.get('idUsuario') || '';
  }, [searchParams]);

  const buscaNome = useMemo(() => {
    return searchParams.get('usuarioNome') || '';
  }, [searchParams]);

  const buscaCpf = useMemo(() => {
    return searchParams.get('nrCpf') || '';
  }, [searchParams]);

  const buscaStatus = useMemo(() => {
    return searchParams.get('usuarioStatus') || '';
  }, [searchParams]);

  const buscaAd = useMemo(() => {
    return searchParams.get('loginAD') || '';
  }, [searchParams]);

  const buscaLogin = useMemo(() => {
    return searchParams.get('login') || '';
  }, [searchParams]);

  const buscaFuncionario = useMemo(() => {
    return searchParams.get('codigoIdentificacaoFuncionario') || '';
  }, [searchParams]);

  const buscaOrderBy = useMemo(() => {
    return searchParams.get('orderBy') || '';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);
  
  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: t('pages.usuario.idUsuario'),
      textoBusca: buscaId,
      handleTextoBusca: texto => { searchParams.set('idUsuario', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.usuario.usuarioNome'),
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('usuarioNome', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextFieldNumeric',
      mapValue: [],
      label: t('pages.usuario.nrCpf'),
      textoBusca: buscaCpf,
      handleTextoBusca: texto => { searchParams.set('nrCpf', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'Select',
      mapValue: listKeyValueUsuarioStatus.map((itemk) => itemk),
      label: t('pages.usuario.usuarioStatus'),
      textoBusca: buscaStatus,
      handleTextoBusca: texto => { searchParams.set('usuarioStatus', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.usuario.loginAD'),
      textoBusca: buscaAd,
      handleTextoBusca: texto => { searchParams.set('loginAD', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.usuario.login'),
      textoBusca: buscaLogin,
      handleTextoBusca: texto => { searchParams.set('login', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.usuario.codigoIdentificacaoFuncionario'),
      textoBusca: buscaFuncionario,
      handleTextoBusca: texto => { searchParams.set('codigoIdentificacaoFuncionario', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
  ];

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString()); 
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleOrdenacao = (campo:string) => {
    searchParams.set('orderBy', campo); 
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({...stateSnack, open:false});
  };

  useEffect(() => {
    setIsLoading(true);
  
    UsuariosService.getAll(searchParams.toString())
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString())/Number.parseInt(limit.toString()));
        }
      });

  },[eventoPesquisar]);

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.usuario.titulo')}
        // botaoLog={
        //   <Button
        //     color='info'
        //     disableElevation
        //     variant='contained'
        //     size='small'
        //     onClick={() => navigate('/usuario/listagem/logs')}
        //     startIcon={<Icon>crop</Icon>}
        //   >
        //     <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
        //       {t('components.ferramentasDaListagem.logs')}
        //     </Typography>
        //   </Button>
        // }
        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('global.acoes')}</TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'idUsuario_ASC'?'idUsuario_DESC':'idUsuario_ASC')}>
                  {t('pages.usuario.idUsuario')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'idUsuario_ASC'?'north':buscaOrderBy === 'idUsuario_DESC'?'south':''}</Icon>
                </TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'UsuarioNome_ASC'?'UsuarioNome_DESC':'UsuarioNome_ASC')}>
                  {t('pages.usuario.usuarioNome')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'UsuarioNome_ASC'?'north':buscaOrderBy === 'UsuarioNome_DESC'?'south':''}</Icon>
                </TableCell>                
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'Login_ASC'?'Login_DESC':'Login_ASC')}>
                  {t('pages.usuario.login')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'Login_ASC'?'north':buscaOrderBy === 'Login_DESC'?'south':''}</Icon>
                </TableCell>    
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'LoginAD_ASC'?'LoginAD_DESC':'LoginAD_ASC')}>
                  {t('pages.usuario.loginAD')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'LoginAD_ASC'?'north':buscaOrderBy === 'LoginAD_DESC'?'south':''}</Icon>
                </TableCell>    
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'NrCpf_ASC'?'NrCpf_DESC':'NrCpf_ASC')}>
                  {t('pages.usuario.nrCpf')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'NrCpf_ASC'?'north':buscaOrderBy === 'NrCpf_DESC'?'south':''}</Icon>
                </TableCell>                
                <TableCell >
                  {t('pages.usuario.eMail')}
                </TableCell>
                <TableCell >
                  {t('pages.usuario.dtUltinoLogin')}
                </TableCell>
                <TableCell >
                  {t('pages.usuario.usuarioStatus')}
                </TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.IdUsuario}>
                  <TableCell>
                    <IconButton size="small" disabled={!hasPermitionRead} onClick={() => navigate(`/usuario/detalhe/${row.IdUsuario}`)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.IdUsuario}</TableCell>
                  <TableCell>{row.UsuarioNome}</TableCell>
                  <TableCell>{row.Login}</TableCell>
                  <TableCell>{row.LoginAD}</TableCell>
                  <TableCell>{row.nrCpf}</TableCell>
                  <TableCell>{row.EMail}</TableCell>
                  <TableCell>{stringToDateTime(row.DataLoginUltimo)}</TableCell>
                  <TableCell>{getValueFromKeyValue(listKeyValueUsuarioStatus, row.UsuarioStatus)}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/usuario/detalhe/novo')}
                      startIcon={<Icon>add</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.novo')}
                      </Typography>
                    </Button>)}
                  </TableCell>
                  <TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5,8,10,20,50,100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>

      </LayoutBaseDePagina>
      <AlertaSnackBar 
        open={stateSnack.open} 
        severity={stateSnack.severity as AlertColor} 
        message={stateSnack.message} 
        handleClose={handleCloseSnack} 
      />
    </>
  );
};